import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";

import api from "../api/axios";
import "../assets/styles/promotions.css";

const PAGE_NAME = "Promotional Popups";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const emptyButton = () => ({ label: "", url: "", style: "primary" });

const emptyForm = () => ({
  name: "",
  title: "",
  message: "",
  audience: "all",
  startsAt: "",
  endsAt: "",
  priority: 0,
  isActive: false,
  imageClickUrl: "",
  imageFile: null,
  existingImageUrl: "",
  removeImage: false,
  buttons: [],
});

const toDateTimeInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16);
};

const toIsoDate = (value) => (value ? new Date(value).toISOString() : "");

const getCampaignStatus = (promotion) => {
  if (!promotion.is_active) return { label: "Inactive", className: "inactive" };

  const now = Date.now();
  const startsAt = promotion.starts_at
    ? new Date(promotion.starts_at).getTime()
    : null;
  const endsAt = promotion.ends_at ? new Date(promotion.ends_at).getTime() : null;

  if (startsAt && startsAt > now) {
    return { label: "Scheduled", className: "scheduled" };
  }
  if (endsAt && endsAt < now) {
    return { label: "Ended", className: "ended" };
  }

  return { label: "Live", className: "live" };
};

const formatDate = (value) => {
  if (!value) return "No limit";
  return new Date(value).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const showRequestError = (error, fallback) => {
  if (error.response?.status === 403) return;
  toast.error(error.response?.data?.message || fallback);
};

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const localImagePreview = useMemo(() => {
    if (!form.imageFile) return "";
    return URL.createObjectURL(form.imageFile);
  }, [form.imageFile]);

  useEffect(
    () => () => {
      if (localImagePreview) URL.revokeObjectURL(localImagePreview);
    },
    [localImagePreview]
  );

  const fetchPromotions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/promotions", {
        headers: getPermissionHeaders("view"),
      });
      setPromotions(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      setPromotions([]);
      showRequestError(error, "Unable to load promotional popups.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (promotion) => {
    setEditingId(promotion.promotion_id);
    setForm({
      name: promotion.name || "",
      title: promotion.title || "",
      message: promotion.message || "",
      audience: promotion.audience || "all",
      startsAt: toDateTimeInput(promotion.starts_at),
      endsAt: toDateTimeInput(promotion.ends_at),
      priority: Number(promotion.priority || 0),
      isActive: Boolean(promotion.is_active),
      imageClickUrl: promotion.image_click_url || "",
      imageFile: null,
      existingImageUrl: promotion.image_url || "",
      removeImage: false,
      buttons: Array.isArray(promotion.buttons)
        ? promotion.buttons.map((button) => ({ ...button }))
        : [],
    });
    setFormOpen(true);
  };

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const updateButton = (index, field, value) => {
    setForm((previous) => ({
      ...previous,
      buttons: previous.buttons.map((button, buttonIndex) =>
        buttonIndex === index ? { ...button, [field]: value } : button
      ),
    }));
  };

  const addButton = () => {
    if (form.buttons.length >= 3) return;
    setForm((previous) => ({
      ...previous,
      buttons: [...previous.buttons, emptyButton()],
    }));
  };

  const removeButton = (index) => {
    setForm((previous) => ({
      ...previous,
      buttons: previous.buttons.filter((_, buttonIndex) => buttonIndex !== index),
    }));
  };

  const savePromotion = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.title.trim()) {
      toast.error("Campaign name and popup title are required.");
      return;
    }

    const incompleteButton = form.buttons.some(
      (button) => !button.label.trim() || !button.url.trim()
    );
    if (incompleteButton) {
      toast.error("Each popup button needs both a label and a link.");
      return;
    }

    const payload = new FormData();
    payload.append("name", form.name.trim());
    payload.append("title", form.title.trim());
    payload.append("message", form.message.trim());
    payload.append("audience", form.audience);
    payload.append("starts_at", toIsoDate(form.startsAt));
    payload.append("ends_at", toIsoDate(form.endsAt));
    payload.append("priority", String(form.priority || 0));
    payload.append("is_active", String(form.isActive));
    payload.append("image_click_url", form.imageClickUrl.trim());
    payload.append("buttons", JSON.stringify(form.buttons));
    payload.append("remove_image", String(form.removeImage));
    if (form.imageFile) payload.append("image", form.imageFile);

    try {
      setSaving(true);
      const config = {
        headers: getPermissionHeaders(editingId ? "edit" : "add"),
      };
      const response = editingId
        ? await api.put(`/admin/promotions/${editingId}`, payload, config)
        : await api.post("/admin/promotions", payload, config);

      toast.success(response.data?.message || "Promotion saved.");
      setFormOpen(false);
      await fetchPromotions();
    } catch (error) {
      showRequestError(error, "Unable to save the promotional popup.");
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (promotion) => {
    confirmDialog({
      header: "Delete promotional popup?",
      message: `Delete “${promotion.name}” and its acknowledgement history?`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          setDeletingId(promotion.promotion_id);
          const response = await api.delete(
            `/admin/promotions/${promotion.promotion_id}`,
            { headers: getPermissionHeaders("delete") }
          );
          toast.success(response.data?.message || "Promotion deleted.");
          await fetchPromotions();
        } catch (error) {
          showRequestError(error, "Unable to delete the promotion.");
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const imagePreview = form.removeImage
    ? ""
    : localImagePreview || form.existingImageUrl;

  return (
    <section className="admin-page promotions-page">
      <ConfirmDialog />

      <div className="page-header">
        <div>
          <p>Customer engagement</p>
          <h1>Promotional Popups</h1>
          <span>Create scheduled offers, announcements, and maintenance notices.</span>
        </div>

        <button type="button" className="primary-action" onClick={openCreate}>
          <i className="pi pi-plus" aria-hidden="true" />
          New popup
        </button>
      </div>

      <div className="page-card promotions-list-card">
        {loading ? (
          <div className="promotions-empty" role="status">
            <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            Loading promotional popups...
          </div>
        ) : promotions.length === 0 ? (
          <div className="promotions-empty">
            <i className="pi pi-megaphone" aria-hidden="true" />
            <h2>No promotional popups yet</h2>
            <p>Create your first offer or customer announcement.</p>
          </div>
        ) : (
          <div className="promotions-grid">
            {promotions.map((promotion) => {
              const status = getCampaignStatus(promotion);

              return (
                <article className="promotion-admin-card" key={promotion.promotion_id}>
                  <div className="promotion-admin-media">
                    {promotion.image_url ? (
                      <img src={promotion.image_url} alt="" />
                    ) : (
                      <i className="pi pi-megaphone" aria-hidden="true" />
                    )}
                    <span className={`promotion-admin-status ${status.className}`}>
                      {status.label}
                    </span>
                  </div>

                  <div className="promotion-admin-body">
                    <p>{promotion.name}</p>
                    <h2>{promotion.title}</h2>
                    <span className="promotion-admin-message">
                      {promotion.message || "Image-only popup"}
                    </span>

                    <div className="promotion-admin-meta">
                      <span><i className="pi pi-users" /> {promotion.audience}</span>
                      <span><i className="pi pi-check-circle" /> {promotion.receipt_count || 0} acknowledged</span>
                      <span><i className="pi pi-sort-amount-up" /> Priority {promotion.priority || 0}</span>
                    </div>

                    <div className="promotion-admin-schedule">
                      <span>Starts: {formatDate(promotion.starts_at)}</span>
                      <span>Ends: {formatDate(promotion.ends_at)}</span>
                    </div>

                    <div className="promotion-admin-actions">
                      <button type="button" onClick={() => openEdit(promotion)}>
                        <i className="pi pi-pencil" /> Edit
                      </button>
                      <button
                        type="button"
                        className="danger"
                        disabled={deletingId === promotion.promotion_id}
                        onClick={() => requestDelete(promotion)}
                      >
                        <i className="pi pi-trash" /> Delete
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <Sidebar
        visible={formOpen}
        position="right"
        className="promotion-form-sidebar"
        onHide={() => !saving && setFormOpen(false)}
        header={editingId ? "Edit promotional popup" : "Create promotional popup"}
      >
        <form className="promotion-admin-form" onSubmit={savePromotion}>
          <label>
            <span>Internal campaign name *</span>
            <input
              value={form.name}
              maxLength={120}
              onChange={(event) => updateField("name", event.target.value)}
              placeholder="October maintenance notice"
            />
          </label>

          <label>
            <span>Popup title *</span>
            <input
              value={form.title}
              maxLength={160}
              onChange={(event) => updateField("title", event.target.value)}
              placeholder="A little update is on the way"
            />
          </label>

          <label>
            <span>Message</span>
            <textarea
              rows="4"
              value={form.message}
              maxLength={2000}
              onChange={(event) => updateField("message", event.target.value)}
              placeholder="The store will be unavailable from 10:00 PM to 10:30 PM."
            />
          </label>

          <div className="promotion-form-grid">
            <label>
              <span>Audience</span>
              <select
                value={form.audience}
                onChange={(event) => updateField("audience", event.target.value)}
              >
                <option value="all">Everyone</option>
                <option value="registered">Registered customers</option>
                <option value="guest">Guest visitors</option>
              </select>
            </label>

            <label>
              <span>Priority</span>
              <input
                type="number"
                value={form.priority}
                onChange={(event) => updateField("priority", event.target.value)}
              />
            </label>

            <label>
              <span>Starts at</span>
              <input
                type="datetime-local"
                value={form.startsAt}
                onChange={(event) => updateField("startsAt", event.target.value)}
              />
            </label>

            <label>
              <span>Ends at</span>
              <input
                type="datetime-local"
                value={form.endsAt}
                onChange={(event) => updateField("endsAt", event.target.value)}
              />
            </label>
          </div>

          <label className="promotion-active-toggle">
            <div>
              <strong>Active campaign</strong>
              <small>The popup only appears while active and within its schedule.</small>
            </div>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => updateField("isActive", event.target.checked)}
            />
          </label>

          <div className="promotion-image-field">
            <span>Popup image</span>
            {imagePreview && <img src={imagePreview} alt="Popup preview" />}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(event) => {
                updateField("imageFile", event.target.files?.[0] || null);
                updateField("removeImage", false);
              }}
            />
            {form.existingImageUrl && !form.imageFile && (
              <button
                type="button"
                className="promotion-remove-image"
                onClick={() => updateField("removeImage", !form.removeImage)}
              >
                {form.removeImage ? "Keep existing image" : "Remove existing image"}
              </button>
            )}
          </div>

          <label>
            <span>Image click link</span>
            <input
              type="text"
              value={form.imageClickUrl}
              onChange={(event) => updateField("imageClickUrl", event.target.value)}
              placeholder="https://example.com/offer or /shop"
            />
          </label>

          <div className="promotion-buttons-editor">
            <div className="promotion-buttons-heading">
              <div>
                <strong>Custom buttons</strong>
                <small>Up to three call-to-action links.</small>
              </div>
              <button type="button" onClick={addButton} disabled={form.buttons.length >= 3}>
                <i className="pi pi-plus" /> Add button
              </button>
            </div>

            {form.buttons.map((button, index) => (
              <div className="promotion-button-row" key={index}>
                <input
                  value={button.label}
                  maxLength={40}
                  onChange={(event) => updateButton(index, "label", event.target.value)}
                  placeholder="Shop now"
                  aria-label={`Button ${index + 1} label`}
                />
                <input
                  value={button.url}
                  onChange={(event) => updateButton(index, "url", event.target.value)}
                  placeholder="/shop"
                  aria-label={`Button ${index + 1} link`}
                />
                <select
                  value={button.style}
                  onChange={(event) => updateButton(index, "style", event.target.value)}
                  aria-label={`Button ${index + 1} style`}
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                  <option value="link">Text link</option>
                </select>
                <button type="button" onClick={() => removeButton(index)} aria-label={`Remove button ${index + 1}`}>
                  <i className="pi pi-times" />
                </button>
              </div>
            ))}
          </div>

          <div className="promotion-form-actions">
            <button type="button" onClick={() => setFormOpen(false)} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="primary-action" disabled={saving}>
              <i className={`pi ${saving ? "pi-spin pi-spinner" : "pi-save"}`} />
              {saving ? "Saving..." : "Save popup"}
            </button>
          </div>
        </form>
      </Sidebar>
    </section>
  );
};

export default Promotions;
